import React, { useCallback, useMemo } from 'react';

/* eslint frontbucket-patterns/no-new-emotion-styled: "warn" */
import styled from '@emotion/styled';
import { useDispatch, useSelector } from 'react-redux';

import Heading from '@atlaskit/heading';
import { Stack, Box } from '@atlaskit/primitives';
import { colors, fontSize, gridSize } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';

import ModalDialog, { ActionProps } from 'src/components/modal-dialog-actions';
import { useIntl } from 'src/hooks/intl';
import commonMessages from 'src/i18n/common';
import {
  getIsDiffSettingsDialogLoading,
  toggleSettingsDialog,
} from 'src/redux/diff-settings';
import { useMatch } from 'src/router/hooks';
import { BucketState } from 'src/types/state';

import { PR_PATH } from '../constants';

import { AnnotationsField } from './annotations-field';
import { ColorBlindModeField } from './color-blind-mode-field';
import { DiffSettingsForm } from './diff-settings-form';
import { DiffViewModeField } from './diff-view-mode-field';
import { FileViewModeField } from './file-view-mode-field';
import messages from './i18n';
import { IgnoreWhitespaceField } from './ignore-whitespace-field';
import { SingleFileModeField } from './single-file-mode-field';
import { Field } from './styled';
import { TabSizeField, tabSizePrefId } from './tab-size-field';
import { WordDiffField } from './word-diff-field';

const componentOverrides = {
  Container: DiffSettingsForm,
};

const SettingsHeading = styled.label<{
  marginTop?: number;
  marginBottom?: number;
}>`
  display: block;
  color: ${token('color.text.subtlest', colors.N200)};
  font-size: ${fontSize() - 2}px;
  margin-left: ${gridSize() / 4}px;
  font-weight: 600;
  margin-top: ${props => `${props.marginTop}px`};
`;

const SettingsSectionHeading = styled.div<{ marginTop?: number }>`
  font-size: ${fontSize()}px;
  margin-left: ${gridSize() / 4}px;
  margin-top: ${props => `${props.marginTop}px`};
  margin-bottom: ${gridSize() * 2}px;
  font-weight: 500;
`;

const SettingsLabel = styled.label({
  display: 'block',
  color: token('color.text.subtlest', colors.N200),
  fontSize: `${fontSize() - 2}px`,
  marginLeft: token('space.025', '2px'),
  fontWeight: 600,
  marginTop: token('space.200', '16px'),
});

const tabSizePrefLabelId = `${tabSizePrefId}-label`;

type Props = {
  disableAnnotations?: boolean;
};

export const DiffSettingsDialog = React.memo((props: Props) => {
  const { disableAnnotations = false } = props;
  const dispatch = useDispatch();
  const intl = useIntl();
  const isLoading = useSelector<BucketState, boolean>(
    getIsDiffSettingsDialogLoading
  );

  const { path } = useMatch();
  const isPrPage = path === PR_PATH;

  const handleClose = useCallback(
    () => dispatch(toggleSettingsDialog(false)),
    [dispatch]
  );

  const actions: ActionProps[] = useMemo(
    () => [
      {
        appearance: 'subtle',
        isDisabled: isLoading,
        onClick: handleClose,
        text: intl.formatMessage(commonMessages.cancel),
      },
      {
        appearance: 'primary',
        isDisabled: isLoading,
        isLoading,
        text: intl.formatMessage(commonMessages.save),
        type: 'submit',
      },
    ],
    [handleClose, intl, isLoading]
  );

  const FieldGroup = ({
    heading,
    children,
  }: {
    heading: string;
    children: React.ReactNode;
  }) => (
    <Box>
      <Stack space="space.100">
        <Box>
          <Heading level="h300">{heading}</Heading>
        </Box>
        <Box>{children}</Box>
      </Stack>
    </Box>
  );

  /* eslint @typescript-eslint/ban-types: "warn" */
  const DiffSettingsModal: React.FC = () => {
    return (
      <ModalDialog
        actions={actions}
        components={componentOverrides}
        heading={
          <Heading level="h600">
            {intl.formatMessage(messages.modalHeading)}
          </Heading>
        }
        onClose={handleClose}
        shouldCloseOnEscapePress={!isLoading}
        shouldCloseOnOverlayClick={!isLoading}
        width="small"
      >
        <Stack space="space.250">
          <FieldGroup heading={intl.formatMessage(messages.diffViewLabel)}>
            <DiffViewModeField autoFocus isDisabled={isLoading} />
          </FieldGroup>
          <FieldGroup heading={intl.formatMessage(messages.showLabel)}>
            <IgnoreWhitespaceField isDisabled={isLoading} />
            <WordDiffField isDisabled={isLoading} />
            <ColorBlindModeField isDisabled={isLoading} />
            <AnnotationsField isDisabled={isLoading} />
          </FieldGroup>
          <FieldGroup
            heading={intl.formatMessage(messages.singleFileModeRadioGroupLabel)}
          >
            <SingleFileModeField isDisabled={isLoading} />
          </FieldGroup>
          <FieldGroup heading={intl.formatMessage(messages.tabSizePrefLabel)}>
            <TabSizeField isDisabled={isLoading} />
          </FieldGroup>
          <FieldGroup
            heading={intl.formatMessage(messages.fileViewRadioGroupLabel)}
          >
            <FileViewModeField isDisabled={isLoading} />
          </FieldGroup>
        </Stack>
      </ModalDialog>
    );
  };

  return (
    <>
      {isPrPage ? (
        <DiffSettingsModal />
      ) : (
        <ModalDialog
          actions={actions}
          components={componentOverrides}
          heading={intl.formatMessage(messages.modalHeading)}
          onClose={handleClose}
          shouldCloseOnEscapePress={!isLoading}
          shouldCloseOnOverlayClick={!isLoading}
          width="small"
        >
          <>
            <SettingsSectionHeading marginTop={10}>
              {intl.formatMessage(messages.filesRadioGroupSectionLabel)}
            </SettingsSectionHeading>
            <SettingsHeading>
              {intl.formatMessage(messages.diffViewModeLabel)}
            </SettingsHeading>
          </>
          <DiffViewModeField autoFocus isDisabled={isLoading} />
          <SettingsHeading marginTop={gridSize() * 2}>
            {intl.formatMessage(messages.showLabel)}
          </SettingsHeading>
          <IgnoreWhitespaceField isDisabled={isLoading} />
          <WordDiffField isDisabled={isLoading} />
          <ColorBlindModeField isDisabled={isLoading} />
          {!disableAnnotations && (
            <Field>
              <AnnotationsField isDisabled={isLoading} />
            </Field>
          )}

          <SettingsHeading marginTop={gridSize() * 2}>
            {intl.formatMessage(messages.singleFileModeRadioGroupLabel)}
          </SettingsHeading>

          <SingleFileModeField isDisabled={isLoading} />
          <SettingsLabel htmlFor={tabSizePrefId} id={tabSizePrefLabelId}>
            {intl.formatMessage(messages.tabSizePrefLabel)}
          </SettingsLabel>
          <Field>
            <TabSizeField isDisabled={isLoading} />
          </Field>
          <SettingsSectionHeading marginTop={gridSize() * 4}>
            {intl.formatMessage(messages.sidebarRadioGroupSectionLabel)}
          </SettingsSectionHeading>
          <SettingsHeading>
            {intl.formatMessage(messages.fileViewRadioGroupLabel)}
          </SettingsHeading>
          <FileViewModeField isDisabled={isLoading} />
        </ModalDialog>
      )}
    </>
  );
});
